import { useMutation } from "@apollo/client/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { constantsData, identifiersData, namesData, statusTypesData } from "@/data";
import { deleteClientRevenueReportsTemplateMutation, getClientRevenueReportsTemplatesListQuery } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useDeleteClientRevenueReportTemplate = (clientId) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [deleteClientRevenueReportTemplateAction, { loading }] = useMutation(
        deleteClientRevenueReportsTemplateMutation,
        {
            awaitRefetchQueries: true,
            refetchQueries: [
                {
                    query: getClientRevenueReportsTemplatesListQuery,
                    variables: { id: clientId },
                },
            ],
        },
    );

    const { deleteClientRevenueReportTemplatePending: deleteClientRevenueReportTemplatePendingToastName } =
        namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const deleteClientRevenueReportTemplate = async (id) => {
        try {
            await deleteClientRevenueReportTemplateAction({
                onCompleted: () => {
                    dispatch(
                        openAlert(
                            t("messages.deleteMsgs.success", { entity: t("labels.template") }),
                            statusTypesData.success,
                        ),
                    );
                },
                variables: {
                    where: { id },
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        } finally {
            toast.dismiss(`${deleteClientRevenueReportTemplatePendingToastName}_${toastId}`);
        }
    };

    useEffect(
        () => {
            if (loading) {
                toast.info(() => t("messages.deleteMsgs.pending", { entity: t("labels.template") }), {
                    toastId: `${deleteClientRevenueReportTemplatePendingToastName}_${toastId}`,
                });
            }
        },
        [loading], //eslint-disable-line
    );

    return {
        deleteClientRevenueReportTemplate,
        loading,
    };
};

export default useDeleteClientRevenueReportTemplate;
